/**
 * Google blocks OAuth inside apps' built-in browsers (403
 * disallowed_useragent), so on `/sign-up` a Google tap there hands the page
 * off to the phone's real browser instead. The UI is
 * `src/pages/signUp/inAppGoogle/InAppGoogleSignUp.tsx`.
 */

export type MobileOs = 'ios' | 'android' | 'other';

/** Query param that carries the PostHog id into the new browser (`PostHogProvider`). */
export const handoffDistinctIdParam = 'ph_id';

/** Set on Android's fallback URL, so a page reloaded in the app opens the sheet. */
export const handoffFailedParam = 'handoff';
const handoffFailedValue = 'failed';

/** The only page that takes a hand-off, so other routes never read `ph_id`. */
const handoffPathname = /^\/sign-up\/?$/;

/** posthog-js makes anonymous ids with UUIDv7 and older versions with v4. */
const uuidShape =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `maxTouchPoints` tells an iPad from a Mac: iPadOS browsers send a
 * `Macintosh` user agent, and only the iPad has a touch screen.
 */
export const detectMobileOs = (
  userAgent: string,
  maxTouchPoints = 0,
): MobileOs => {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'other';
};

/** Whether Android's intent fallback reloaded the page in the app (`search` is `location.search`). */
export const isHandoffFailed = (search: string) =>
  new URLSearchParams(search).get(handoffFailedParam) === handoffFailedValue;

/**
 * `search` without the fallback marker. The marker is only for the page the
 * fallback reloads, so it must not travel on into links the visitor copies or
 * the LinkedIn return path. Returns the query without a leading `?`.
 */
export const searchWithoutHandoffFailed = (search: string) => {
  const params = new URLSearchParams(search);
  params.delete(handoffFailedParam);
  return params.toString();
};

/** `href` without the fallback marker, for the link the sheet copies. */
export const urlWithoutHandoffFailed = (href: string) => {
  const url = new URL(href);
  url.search = searchWithoutHandoffFailed(url.search);
  return url.toString();
};

interface ReadHandoffDistinctIdInput {
  href: string;
  /** `$user_state` from the browser's saved PostHog data, if it has any. */
  persistedUserState?: string | null;
}

/**
 * The PostHog id a hand-off carries in `?ph_id=`, or nothing when the page
 * must not adopt it. Anyone can craft a link, so the id is only taken on
 * `/sign-up`, only when it looks like a PostHog id, and never over a browser
 * that already knows an identified user: bootstrapping would replace that
 * identity with the one in the link.
 */
export const readHandoffDistinctId = ({
  href,
  persistedUserState,
}: ReadHandoffDistinctIdInput) => {
  const url = new URL(href);
  if (!handoffPathname.test(url.pathname)) return undefined;
  const distinctId = url.searchParams.get(handoffDistinctIdParam);
  if (!distinctId || !uuidShape.test(distinctId)) return undefined;
  if (persistedUserState && persistedUserState !== 'anonymous') {
    return undefined;
  }
  return distinctId;
};

// The cookie holds the same JSON, URI-encoded.
const parseEntry = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return JSON.parse(decodeURIComponent(raw));
  }
};

/**
 * What the browser's saved PostHog data says about the visitor, from the raw
 * `ph_<token>_posthog` values in localStorage and the cookie (each is JSON,
 * the cookie URI-encoded). Identified wins if either says so, and data that
 * can't be read counts as identified, so the id in a link is never trusted
 * over something we couldn't check.
 */
export const readPersistedUserState = (
  ...rawEntries: (string | null | undefined)[]
) => {
  let state: string | undefined;
  for (const raw of rawEntries) {
    if (!raw) continue;
    try {
      const entry: unknown = parseEntry(raw);
      const userState =
        entry && typeof entry === 'object' && '$user_state' in entry
          ? entry.$user_state
          : undefined;
      if (userState === 'identified') return 'identified';
      if (userState === 'anonymous') state = 'anonymous';
    } catch {
      return 'identified';
    }
  }
  return state;
};

/**
 * The page URL with the PostHog id added, so a visit on one browser or device
 * and the sign-up on another stay one person. Every other param (trial offer
 * code, UTMs) rides along. If someone forwards it to a colleague, the two
 * merge into one PostHog person: accepted, since the link is meant for the
 * visitor's own devices.
 */
export const buildContinueUrl = (href: string, distinctId?: string) => {
  const url = new URL(href);
  url.searchParams.delete(handoffFailedParam);
  if (distinctId) url.searchParams.set(handoffDistinctIdParam, distinctId);
  return url.toString();
};

/**
 * The URL that asks the phone to open `href` in its real browser, with the
 * PostHog id added so the visit and the sign-up stay one person. Every other
 * param (trial offer code, UTMs) rides along in `href`.
 *
 * - Android: an `intent://` URL without a package, so the default browser
 *   opens (Chrome, Samsung Internet...). If nothing handles it, the in-app
 *   browser loads the fallback URL, marked so the page opens the sheet.
 * - iOS: `x-safari-https://` opens Safari on iOS 17+ in many in-app
 *   browsers. It's undocumented, so the sheet always shows as well.
 */
export const buildHandoffUrl = (
  href: string,
  os: Exclude<MobileOs, 'other'>,
  distinctId?: string,
) => {
  const url = new URL(buildContinueUrl(href, distinctId));
  const target = `${url.host}${url.pathname}${url.search}`;

  if (os === 'ios') return `x-safari-https://${target}`;

  const fallback = new URL(url);
  fallback.searchParams.delete(handoffDistinctIdParam);
  fallback.searchParams.set(handoffFailedParam, handoffFailedValue);
  return `intent://${target}#Intent;scheme=https;S.browser_fallback_url=${encodeURIComponent(fallback.toString())};end`;
};
